import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { clearSession, setUnauthenticatedHandler, storeSession, storedSession } from "../lib/api.js";

const AuthContext = createContext(null);

/**
 * AuthProvider owns the signed-in operator.
 *
 * The session is restored from storage on load and then verified against the
 * server. Trusting the stored copy alone would leave the console rendering a
 * full navigation for someone whose account was disabled an hour ago.
 */
export function AuthProvider({ children }) {
  const [session, setSession] = useState(() => storedSession());
  const [checking, setChecking] = useState(Boolean(storedSession()));

  const signOut = useCallback(async ({ notifyServer = true } = {}) => {
    if (notifyServer) {
      try {
        await api.logout();
      } catch {
        // A failed logout must not trap somebody in the console; the local
        // credential is discarded either way.
      }
    }
    clearSession();
    setSession(null);
  }, []);

  useEffect(() => {
    setUnauthenticatedHandler(() => setSession(null));
  }, []);

  useEffect(() => {
    if (!session?.token) {
      setChecking(false);
      return;
    }
    let cancelled = false;
    api
      .whoami()
      .then((me) => {
        if (cancelled) return;
        setSession((current) => ({ ...current, ...me }));
      })
      .catch(() => {
        if (!cancelled) {
          clearSession();
          setSession(null);
        }
      })
      .finally(() => !cancelled && setChecking(false));
    return () => {
      cancelled = true;
    };
    // Verification runs once per mount; re-running on every session change
    // would loop, because the check itself updates the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = useCallback(async (challengeId, code) => {
    const result = await api.verifyLogin(challengeId, code);
    const next = {
      token: result.token,
      user_id: result.user_id,
      role: result.role,
      email: result.email,
      expiresAt: result.expires_at,
    };
    storeSession(next);
    setSession(next);
    return next;
  }, []);

  const value = useMemo(
    () => ({
      session,
      checking,
      signIn,
      signOut,
      isAdmin: session?.role === "SYSTEM_ADMIN",
      canWrite: session?.role === "SYSTEM_ADMIN" || session?.role === "OPERATOR",
    }),
    [session, checking, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside an AuthProvider");
  return ctx;
}
